import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./SeriesDetails.css";
import paintingsData from "../data/paintings.json";

// --- SUB-COMPONENT: Individual Series Piece ---
// This handles the "Read More" state independently for each painting in the list.
const SeriesPiece = ({ painting, index, navigate, fadeUp }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Text Truncation & HTML Handling Logic
    const rawStory = painting.story || "A profound narrative captured through unparalleled texture and form.";
    const stripHtml = (html) => html.replace(/<[^>]*>?/gm, '');
    const plainTextStory = stripHtml(rawStory);
    const maxLength = 250; 
    const isLongText = plainTextStory.length > maxLength;

    return (
        <motion.div 
            className={`breakdown-item ${index % 2 !== 0 ? 'reverse' : ''}`}
            initial="hidden" 
            whileInView="visible" 
            viewport={{once: true, amount: 0.15}} 
            variants={fadeUp}
        >
            <div className="breakdown-img-wrapper" onClick={() => navigate(`/originalDetails/${painting.id}`)}>
                <img src={painting.images?.[0]?.imageUrl} alt={painting.title} />
                <div className="expand-hint">View Details</div>
            </div>
            
            <div className="breakdown-data">
                <span className="piece-number">Volume 0{index + 1}</span>
                <h3>{painting.title}</h3>
                <p className="piece-specs">{painting.material} • {painting.size} • {painting.year}</p>
                
                {/* HTML Story with Read More Toggle */}
                <div className="piece-story-container">
                    {isExpanded || !isLongText ? (
                        <div className="piece-story html-formatted" dangerouslySetInnerHTML={{ __html: rawStory }} />
                    ) : (
                        <p className="piece-story" style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                            {plainTextStory.substring(0, maxLength)}...
                        </p>
                    )}

                    {isLongText && (
                        <span 
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{
                                color: "#c4a36e",
                                cursor: "pointer",
                                fontWeight: "500",
                                fontSize: "0.9rem",
                                display: "inline-block",
                                marginTop: "12px"
                            }}
                        >
                            {isExpanded ? "Read Less" : "Read More"}
                        </span>
                    )}
                </div>

                <button 
                    className="btn-elite-outline btn-small-view" 
                    onClick={() => navigate(`/originalDetails/${painting.id}`)}
                    style={{ marginTop: "25px" }}
                >
                    Inspect Piece
                </button>
            </div>
        </motion.div>
    );
};

// --- MAIN PAGE COMPONENT ---
const SeriesDetails = () => {
    const { seriesName } = useParams();
    const navigate = useNavigate();
    
    const [seriesPaintings, setSeriesPaintings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const filtered = paintingsData.filter(p => p.series === seriesName);
        setSeriesPaintings(filtered);
        setLoading(false);
    }, [seriesName]);

    if (loading) return <div className="elite-loader" style={{paddingTop: "20vh"}}>Retrieving Anthology...</div>;
    if (seriesPaintings.length === 0) return <div className="elite-empty-state" style={{paddingTop: "20vh"}}>Anthology not found.</div>;

    const seriesDesc = seriesPaintings[0]?.seriesDescription || "A curated collection of works unified by a singular narrative and artistic vision. Available exclusively as an unbroken set.";
    
    // Dynamically calculate the creation era
    const years = seriesPaintings.map(p => p.year).filter(Boolean);
    const creationEra = years.length > 0 
        ? (Math.min(...years) === Math.max(...years) ? Math.min(...years) : `${Math.min(...years)} - ${Math.max(...years)}`) 
        : "Contemporary";

    // Check if any piece in the series is sold
    const isVaulted = seriesPaintings.some(p => p.sold);

    const whatsappMessage = `Dear Studio,\n\nI am writing to formally request the private pricing and acquisition dossier for the complete '${seriesName}' anthology.\n\nPlease let me know the next steps.`;

    const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } } };

    return (
        <div className="series-details-page">
            <MobileNav />

            {/* HERO BANNER */}
            <div className="series-hero">
                <div className="series-hero-bg" style={{backgroundImage: `url(${seriesPaintings[0]?.images?.[0]?.imageUrl})`}}></div>
                <div className="series-hero-overlay"></div>
                <motion.div className="series-hero-content" initial="hidden" animate="visible" variants={fadeUp}>
                    <span className="series-eyebrow">Complete Anthology</span>
                    <h1 className="series-hero-title">{seriesName}</h1>
                    <div className="series-line"></div>
                </motion.div>
            </div>

            <main className="series-main-content">
                
                {/* THE DOSSIER HEADER */}
                <div className="series-dossier-intro">
                    <motion.div className="dossier-text" initial="hidden" whileInView="visible" viewport={{once: true}} variants={fadeUp}>
                        <span className="curatorial-tag-gold" style={{color: "#c4a36e", textTransform: "uppercase", letterSpacing: "4px", fontSize: "0.75rem", fontWeight: "500"}}>The Narrative</span>
                        <h2>Curatorial Vision</h2>
                        <p>{seriesDesc}</p>
                    </motion.div>
                </div>

                {/* SERIES SPECIFICATIONS GRID */}
                <motion.div className="series-specs-grid" initial="hidden" whileInView="visible" viewport={{once: true}} variants={fadeUp}>
                    <div className="spec-item">
                        <span className="spec-label">Anthology Size</span>
                        <span className="spec-value">{seriesPaintings.length} Original Masterworks</span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">Creation Era</span>
                        <span className="spec-value">{creationEra}</span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">Medium Focus</span>
                        <span className="spec-value">{seriesPaintings[0]?.material || "Mixed Media"}</span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">Status</span>
                        <span className="spec-value">{isVaulted ? "Vaulted (Acquired)" : "Available for Acquisition"}</span>
                    </div>
                </motion.div>

                {/* THE INDIVIDUAL PIECES */}
                <div className="series-breakdown">
                    <h2 className="breakdown-title">The Masterworks</h2>
                    <div className="breakdown-list">
                        {seriesPaintings.map((painting, index) => (
                            <SeriesPiece 
                                key={painting.id} 
                                painting={painting} 
                                index={index} 
                                navigate={navigate} 
                                fadeUp={fadeUp} 
                            />
                        ))}
                    </div>
                </div>

                {/* PROVENANCE, TERMS & ACQUISITION */}
                <motion.div className="series-acquisition-section" initial="hidden" whileInView="visible" viewport={{once: true}} variants={fadeUp}>
                    
                    {/* Trust & Provenance Details */}
                    <div className="series-provenance-info">
                        <h3 className="provenance-title">Provenance & Authenticity</h3>
                        
                        <div className="provenance-grid">
                            <div className="prov-item">
                                <i className="fa-solid fa-certificate"></i>
                                <h4>Studio Certification</h4>
                                <p>This anthology includes a master Certificate of Authenticity, hand-signed and sealed by Anjaan Vivek, guaranteeing the originality and long-term investment value of the complete set.</p>
                            </div>
                            <div className="prov-item">
                                <i className="fa-solid fa-link"></i>
                                <h4>Unbroken Narrative</h4>
                                <p>To preserve the artist's original vision and conceptual fluidity, works within this specific anthology are strictly offered as a singular, unbroken collection and cannot be acquired individually.</p>
                            </div>
                            <div className="prov-item">
                                <i className="fa-solid fa-plane-shield"></i>
                                <h4>White-Glove Logistics</h4>
                                <p>The studio handles end-to-end global logistics. The masterworks will be custom-crated in archival materials and delivered securely via elite fine art couriers, fully insured to your estate.</p>
                            </div>
                        </div>
                    </div>

                    {/* Acquisition Box */}
                    {!isVaulted && (
                        <div className="series-inquiry-box">
                            <h3>Private Acquisition</h3>
                            <p>Private pricing and detailed provenance dossiers for the complete {seriesPaintings.length}-piece '{seriesName}' anthology are available exclusively upon private request.</p>
                            
                            <a href={`https://wa.me/919300847841?text=${encodeURIComponent(whatsappMessage)}`} target="_blank" rel="noreferrer" className="btn-elite-primary acquire-btn-full">
                                <i className="fa-brands fa-whatsapp"></i> Request Anthology Dossier
                            </a>
                        </div>
                    )}

                </motion.div>

            </main>
            <Footer1 />
        </div>
    );
};

export default SeriesDetails;